import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  ButtonBase,
  Chip,
  CircularProgress,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HourglassEmptyOutlinedIcon from "@mui/icons-material/HourglassEmptyOutlined";
import LocalActivityOutlinedIcon from "@mui/icons-material/LocalActivityOutlined";
import RadioButtonUncheckedOutlinedIcon from "@mui/icons-material/RadioButtonUncheckedOutlined";
import {
  getClaimDisplayState,
  getClaimResultPresentation,
  getClaimTicketPath,
  subscribeMyRaffleClaims,
  sortClaimsForDisplay,
} from "../../../services/claimsService";
import { useAuth } from "../../../contexts/AuthContext";
import { getClaimHeroImageAspectRatioCss } from "../../../utils/claimHeroImage";

function resolveBusinessName(business = {}) {
  return String(business?.companyName || business?.displayName || business?.name || "").trim();
}

function formatDate(value) {
  if (!value) return "";
  const parsed = new Date(value);
  if (!Number.isFinite(parsed.getTime())) return "";
  return parsed.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function resolveToneColor(theme, tone) {
  if (tone === "success") return theme.palette.success.main;
  if (tone === "warning") return theme.palette.warning.main;
  if (tone === "info") return theme.palette.info.main;
  return theme.palette.text.secondary;
}

function ResultIcon({ icon, sx }) {
  if (icon === "check") return <CheckCircleOutlineIcon sx={sx} />;
  if (icon === "pending") return <HourglassEmptyOutlinedIcon sx={sx} />;
  return <RadioButtonUncheckedOutlinedIcon sx={sx} />;
}

function ClaimListItem({ claim }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const state = getClaimDisplayState(claim);
  const businessName = resolveBusinessName(claim?.business || {});
  const businessInitial = businessName ? businessName.charAt(0).toUpperCase() : "I";
  const heroImageUrl = String(claim?.heroImageUrl || "").trim();
  const heroImageAspectRatio = getClaimHeroImageAspectRatioCss(claim?.heroImage);
  const ticketPath = getClaimTicketPath(claim);
  const isCoupon = claim?.campaignType === "coupon";
  const result = getClaimResultPresentation({
    ...claim,
    prizeTitle: claim?.prizeSnapshot?.title || claim?.ticket?.prizeSnapshot?.title,
  });
  const resultColor = resolveToneColor(theme, result.tone);
  const chipColor = result.tone === "primary" ? "success" : result.tone;
  const claimedAtText = formatDate(claim?.claimedAt) || "recently";
  const expirationText = claim?.claimEndAt
    ? `${state.isExpired ? "Expired" : "Available through"} ${formatDate(claim.claimEndAt)}`
    : "";

  return (
    <ButtonBase
      data-testid="claim-list-row"
      onClick={() => ticketPath && navigate(ticketPath)}
      sx={{
        width: "100%",
        alignItems: "stretch",
        justifyContent: "flex-start",
        textAlign: "left",
        gap: { xs: 1.25, sm: 1.5 },
        p: { xs: 1, sm: 1.25 },
        borderRadius: 1,
        border: `1px solid ${alpha(resultColor, theme.palette.mode === "dark" ? 0.36 : 0.22)}`,
        bgcolor: "background.paper",
        boxShadow: theme.palette.mode === "dark" ? "0 18px 42px rgba(0,0,0,0.24)" : "0 10px 24px rgba(15,23,42,0.05)",
        transition: theme.transitions.create(["border-color", "box-shadow", "transform", "background-color"], {
          duration: theme.transitions.duration.shorter,
        }),
        "&:hover": {
          borderColor: alpha(resultColor, theme.palette.mode === "dark" ? 0.58 : 0.4),
          bgcolor: alpha(resultColor, theme.palette.mode === "dark" ? 0.08 : 0.045),
          boxShadow: theme.palette.mode === "dark" ? "0 22px 52px rgba(0,0,0,0.34)" : "0 14px 30px rgba(15,23,42,0.08)",
          transform: "translateY(-1px)",
        },
      }}
    >
      <Box
        sx={{
          width: { xs: 72, sm: 88 },
          minWidth: { xs: 72, sm: 88 },
          borderRadius: 1,
          overflow: "hidden",
          position: "relative",
          alignSelf: "stretch",
          bgcolor: alpha(resultColor, theme.palette.mode === "dark" ? 0.18 : 0.1),
        }}
      >
        {heroImageUrl ? (
          <Box
            component="img"
            src={heroImageUrl}
            alt={claim?.title || "Claim"}
            sx={{ width: "100%", height: "100%", minHeight: { xs: 92, sm: 104 }, objectFit: "cover", aspectRatio: heroImageAspectRatio }}
          />
        ) : (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              minHeight: { xs: 92, sm: 104 },
              display: "grid",
              placeItems: "center",
            }}
          >
            <LocalActivityOutlinedIcon sx={{ fontSize: 34, color: resultColor }} />
          </Box>
        )}
        <Box
          sx={{
            position: "absolute",
            right: 6,
            bottom: 6,
            width: 28,
            height: 28,
            borderRadius: "50%",
            display: "grid",
            placeItems: "center",
            color: theme.palette.getContrastText(resultColor),
            bgcolor: resultColor,
            boxShadow: "0 8px 18px rgba(0,0,0,0.24)",
          }}
        >
          <ResultIcon icon={result.icon} sx={{ fontSize: 18 }} />
        </Box>
      </Box>

      <Stack spacing={0.55} sx={{ flex: 1, minWidth: 0, py: { xs: 0.25, sm: 0.5 } }}>
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
          <Chip
            label={result.eyebrow}
            color={chipColor}
            size="small"
            sx={{
              height: 22,
              fontSize: 11,
              fontWeight: 800,
              borderRadius: 1,
              flexShrink: 0,
            }}
          />
          <Typography variant="caption" color="text.secondary" noWrap>
            {isCoupon ? "Coupon" : "Raffle"} claimed {claimedAtText}
          </Typography>
        </Stack>

        <Typography
          variant="subtitle1"
          sx={{
            color: resultColor,
            fontWeight: 900,
            lineHeight: 1.12,
            letterSpacing: 0,
          }}
        >
          {result.title}
        </Typography>

        <Typography variant="body1" sx={{ fontWeight: 800, lineHeight: 1.18 }} noWrap>
          {claim?.title || (isCoupon ? "Coupon" : "Raffle")}
        </Typography>

        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
          <Avatar
            src={String(claim?.business?.avatarUrl || "").trim() || undefined}
            sx={{
              width: 22,
              height: 22,
              fontSize: 11,
              bgcolor: alpha(theme.palette.success.main, 0.18),
              color: theme.palette.success.main,
              flexShrink: 0,
            }}
          >
            {businessInitial}
          </Avatar>
          <Typography variant="body2" color="text.secondary" noWrap>
            {businessName || "Inssa"}
            {expirationText ? ` • ${expirationText}` : ""}
          </Typography>
        </Stack>
      </Stack>

      <Stack
        spacing={0.75}
        alignItems="flex-end"
        justifyContent="center"
        sx={{ display: { xs: "none", sm: "flex" }, maxWidth: 140, color: "text.secondary" }}
      >
        <Typography variant="caption" sx={{ fontWeight: 800, textAlign: "right" }}>
          {result.actionLabel || "Open"}
        </Typography>
        <ChevronRightRoundedIcon sx={{ fontSize: 22 }} />
      </Stack>
    </ButtonBase>
  );
}

export default function ClaimsPage() {
  const { currentUser } = useAuth();
  const [tab, setTab] = React.useState("coupon");
  const [claims, setClaims] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [errorMessage, setErrorMessage] = React.useState("");

  React.useEffect(() => {
    let hasLoaded = false;
    setLoading(true);
    setErrorMessage("");

    return subscribeMyRaffleClaims({
      userUid: currentUser?.uid,
      onNext: (nextClaims) => {
        hasLoaded = true;
        setClaims(nextClaims);
        setErrorMessage("");
        setLoading(false);
      },
      onError: (error) => {
        setErrorMessage(error?.message || "Unable to load your claims.");
        if (!hasLoaded) setLoading(false);
      },
    });
  }, [currentUser?.uid]);

  const visibleClaims = sortClaimsForDisplay(
    claims.filter((claim) => claim?.campaignType === tab)
  );

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, width: "100%" }}>
      <Stack spacing={2.5}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 850, letterSpacing: 0 }}>
            My Claims
          </Typography>
          <Typography color="text.secondary">
            Coupons and raffle tickets you have claimed across Inssa.
          </Typography>
        </Box>

        <Tabs value={tab} onChange={(_, next) => setTab(next)} variant="fullWidth">
          <Tab value="coupon" label="Coupons" />
          <Tab value="raffle" label="Raffles" />
        </Tabs>

        {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}

        {loading ? (
          <Box sx={{ display: "grid", placeItems: "center", py: 7 }}>
            <CircularProgress />
          </Box>
        ) : visibleClaims.length ? (
          <Stack data-testid="claims-list" spacing={1.25}>
            {visibleClaims.map((claim) => (
              <ClaimListItem key={claim.id} claim={claim} />
            ))}
          </Stack>
        ) : (
          <Box
            sx={{
              py: 7,
              px: 2,
              textAlign: "center",
              border: (theme) => `1px dashed ${alpha(theme.palette.text.secondary, 0.3)}`,
              borderRadius: 1,
            }}
          >
            <LocalActivityOutlinedIcon sx={{ fontSize: 52, color: "text.secondary", mb: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              No {tab === "coupon" ? "coupons" : "raffle tickets"} yet
            </Typography>
            <Typography color="text.secondary">
              Your claimed {tab === "coupon" ? "offers" : "entries"} will show up here.
            </Typography>
          </Box>
        )}
      </Stack>
    </Box>
  );
}
